import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import { type RefObject, useState } from 'react';
import type { IZoneMapFeatureProperties } from '@/types/api';
import { LayerId } from '../constants';
import { dealFeatureAt } from '../utils/dealHitTest';
import { namedPlaceAt, type PointOfInterestPlace, placeAt } from '../utils/pointOfInterest';

/**
 * Layers that outrank zones for click selection: a click landing on a station
 * marker, a location pin, a cluster chip, or a basemap place resolves to that
 * feature, not the zone underneath it. Deal marks outrank zones too, but
 * through `dealFeatureAt` — their hit box is padded, so they cannot share this
 * exact-point query. Places are a basemap featureset, same reason.
 */
const ZONE_CLICK_PRIORITY_LAYERS = [
  LayerId.COMPETITORS_TILES,
  LayerId.CLUSTER_CHIP,
  LayerId.LOCATION_UNCLUSTERED,
];

export interface SelectedZone {
  properties: IZoneMapFeatureProperties;
  lngLat: { lng: number; lat: number };
}

/**
 * Click selection for the hunting-zones fill layer. Zones have no hover
 * highlight — only a pointer cursor on hover to signal they're clickable,
 * and the actual selection happens on click.
 *
 * Click outside every zone: deselects.
 *
 * Click: resolves the clicked feature's `IZoneMapFeatureProperties` + `lngLat`
 *        into `selectedZone` state. The caller (MapShell) is responsible for
 *        anchoring a Mapbox Popup at `lngLat` via ReactDOM.createPortal so the
 *        popup tracks pan/zoom natively.
 *
 * Zones are the lowest-priority clickable layer: if a station, a location
 * pin/cluster, or a place sits at the same point, that feature wins and the
 * zone must not also open. A place inside the polygon is opened from this
 * click — see `onPlaceRef` — because the fill listener fires for the whole
 * polygon and the place interaction misses the painted icon.
 * `suppressSelectionRef` covers the same idea for a different kind of
 * competing click consumer (e.g. an armed map-click tool) — see its param doc.
 *
 * Feature properties are cast with `as IZoneMapFeatureProperties` at the Mapbox
 * layer boundary. Mapbox serialises absent GeoJSON properties as `null`, so all
 * consumers must treat null and undefined identically (use `!= null` / `??`).
 * React's rendering escapes all text nodes, and ZoneMapPopup guards the Notion
 * URL with `isSafeHttpUrl`, so this cast is safe at the display layer.
 *
 * All event listeners and cursor state are fully cleaned up by the function
 * returned from `setupZoneInteractions()`. Call that cleanup on project
 * switch, overlay-flag-off, and unmount.
 */
export function useZoneInteractions(
  map: RefObject<MapboxMap | null>,
  /**
   * When `.current` is true at click time, the click is claimed by something
   * else (e.g. the add-pin tool) and must not also select a zone underneath.
   *
   * This has to be a ref, not a boolean prop: `setupZoneInteractions()` binds
   * `handleClick` to the map ONCE (see the effect in MapShell, keyed on
   * `[zonesEnabled, mapId]`), so a plain prop would be captured at bind time
   * and go stale the moment the armed state changes. A ref read at call time
   * is always current.
   *
   * It also has to be checked synchronously, right here, rather than by the
   * caller after `selectedZone` changes: an armed map-click tool typically
   * disarms itself synchronously within the SAME click (see
   * `MapPinClickLayer`/`handlePinClick` in MapSurface). React batches that
   * disarm with this click's `setSelectedZone` into one render, so by the
   * time a `useEffect` reacting to `selectedZone` runs, the tool already
   * looks disarmed — checking here, before any state update, sees the
   * pre-click value instead.
   */
  suppressSelectionRef?: RefObject<boolean>,
  /**
   * Called when a click outside every zone deselects. A separate seam from the popup state above
   * because the consumer's own selection can outlive it: on the map surface a zone is `?zone=`,
   * which a deep link sets without any click, so nothing here would clear it. Not derived from
   * `selectedZone` going null either — that also happens on teardown (overlay off, map switch,
   * unmount), which must not drop the consumer's selection.
   */
  onZoneDeselected?: () => void,
  /**
   * Opens the place under the pointer. Read at click time: the listeners bind
   * once, so a callback captured then would go stale. The fill's own click
   * fires for every point inside the polygon, and the place interaction only
   * fires when a point query hits the icon. A click on the painted glyph does
   * the first and misses the second, so the place has to open from here.
   */
  onPlaceRef?: RefObject<((place: PointOfInterestPlace) => void) | null>,
) {
  const [selectedZone, setSelectedZone] = useState<SelectedZone | null>(null);

  function clearSelection() {
    setSelectedZone(null);
  }

  function handleMouseEnter() {
    const currentMap = map.current;
    if (!currentMap) return;

    currentMap.getCanvas().style.cursor = 'pointer';
  }

  function handleMouseLeave() {
    const currentMap = map.current;
    if (!currentMap) return;

    currentMap.getCanvas().style.cursor = '';
  }

  /** A station, deal, or location pin at the point. Those keep the click; a place does not. */
  function markOwns(point: MapMouseEvent['point']): boolean {
    const currentMap = map.current;
    if (!currentMap) return false;

    // A deal sits on top of the zone that contains it, and it is the smaller target: clicking one
    // must open the deal rather than select the zone underneath. Asked separately from the layers
    // below because the deal hit box is padded — see DEAL_HIT_PADDING_PX.
    if (dealFeatureAt(currentMap, point)) return true;

    // queryRenderedFeatures errors out (and returns []) for the WHOLE call if any
    // requested layer id doesn't currently exist — e.g. the cluster layers are
    // only added with a workspace active. Filter to layers that exist first, the
    // same guard Mapbox's own delegated click listeners use internally.
    const existingPriorityLayers = ZONE_CLICK_PRIORITY_LAYERS.filter((layerId) =>
      currentMap.getLayer(layerId),
    );
    if (!existingPriorityLayers.length) return false;

    return currentMap.queryRenderedFeatures(point, { layers: existingPriorityLayers }).length > 0;
  }

  /** Whether a layer that outranks zones holds the point, and so owns this click. */
  function ownedByAHigherLayer(point: MapMouseEvent['point']): boolean {
    const currentMap = map.current;
    if (!currentMap) return false;
    if (markOwns(point)) return true;
    // The place icon paints above the zone fill, so a click on it is not "outside".
    return namedPlaceAt(currentMap, point);
  }

  /**
   * The outline polygon is not painted below its zoom, so the hexagon is the only thing under
   * the cursor. A click on it is the selection, not a click off it.
   */
  function clickHitsSelectedZone(point: MapMouseEvent['point']): boolean {
    const currentMap = map.current;
    if (!currentMap) return false;
    const layers = [
      LayerId.SELECTED_ZONE_FILL,
      LayerId.SELECTED_ZONE_OUTLINE,
      LayerId.SELECTED_ZONE_MARKER,
    ].filter((layerId) => currentMap.getLayer(layerId));
    if (!layers.length) return false;
    return currentMap.queryRenderedFeatures(point, { layers }).length > 0;
  }

  /**
   * A click that lands on no zone deselects, the way a click off a deal mark closes its card (see
   * useDealsInteractions' preclick). The fill layer's own click never fires out there, so the
   * deselect has to ride the map-wide 'preclick' that precedes it. A click that some other layer
   * owns is not "outside": a station keeps the zone, and a deal or a place closes it from the
   * surface (selectDeal / onPlaceOpen) rather than from this handler.
   */
  function handlePreclick(event: MapMouseEvent) {
    const currentMap = map.current;
    if (!currentMap || suppressSelectionRef?.current) return;
    if (!currentMap.getLayer(LayerId.ZONES_FILL)) return;
    if (currentMap.queryRenderedFeatures(event.point, { layers: [LayerId.ZONES_FILL] }).length) {
      return;
    }
    if (clickHitsSelectedZone(event.point)) return;
    if (ownedByAHigherLayer(event.point)) return;

    setSelectedZone(null);
    onZoneDeselected?.();
  }

  function handleClick(event: MapMouseEvent) {
    const feature = event.features?.[0];
    if (!feature) return;

    if (suppressSelectionRef?.current) return;
    // A station or a pin is the smaller target. Checked before the place: the place box is padded,
    // and a station sitting on an icon must keep the click.
    if (markOwns(event.point)) return;

    const currentMap = map.current;
    const place = currentMap ? placeAt(currentMap, event.point) : null;
    if (place) {
      onPlaceRef?.current?.(place);
      return;
    }

    const properties = feature.properties as IZoneMapFeatureProperties;
    const lngLat = { lng: event.lngLat.lng, lat: event.lngLat.lat };

    setSelectedZone({ properties, lngLat });
  }

  function setupZoneInteractions(): () => void {
    const currentMap = map.current;
    if (!currentMap) return () => {};

    // Bind handlers so we have stable references for cleanup
    const boundEnter = (_e: MapMouseEvent) => handleMouseEnter();
    const boundLeave = (_e: MapMouseEvent) => handleMouseLeave();
    const boundClick = (e: MapMouseEvent) => handleClick(e);
    const boundPreclick = (e: MapMouseEvent) => handlePreclick(e);

    currentMap.on('mouseenter', LayerId.ZONES_FILL, boundEnter);
    currentMap.on('mouseleave', LayerId.ZONES_FILL, boundLeave);
    currentMap.on('click', LayerId.ZONES_FILL, boundClick);
    currentMap.on('preclick', boundPreclick);

    return () => {
      currentMap.off('mouseenter', LayerId.ZONES_FILL, boundEnter);
      currentMap.off('mouseleave', LayerId.ZONES_FILL, boundLeave);
      currentMap.off('click', LayerId.ZONES_FILL, boundClick);
      currentMap.off('preclick', boundPreclick);

      // The map may already be torn down when this cleanup runs — e.g. when
      // the route unmounts the MapShell and useMap calls map.remove() before
      // this effect's cleanup. A removed map
      // has no canvas (getCanvas() returns undefined) and no style, so touching
      // either would throw "Cannot read properties of undefined (reading
      // 'style')" mid-unmount and crash the whole route (→ ErrorBoundary).
      // The listeners above are already gone with the map; skip the rest.
      const canvas = currentMap.getCanvas?.();
      if (!canvas) return;

      // Reset cursor
      canvas.style.cursor = '';
    };
  }

  return { setupZoneInteractions, selectedZone, clearSelection };
}
